<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button } from '../polaris/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../polaris/ui/table';
import { pinnedCell, pinnedRow } from '../polaris/cards/table-pin';

const props = defineProps({
  headers: {
    type: Array,
    default: () => [],
  },
  items: {
    type: Array,
    default: () => [],
  },
  noDataMessage: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // The last column holds the row menu: pinned to the end edge, its title read only by screen readers.
  pinLastColumn: {
    type: Boolean,
    default: false,
  },
  // A search is on: the empty row offers to clear it (emits `clear`).
  clearable: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['clear']);

const { t } = useI18n();

const hasHeaderSlot = computed(() => !!props.headers.length);
const showHeaders = computed(
  () => hasHeaderSlot.value && (props.items.length > 0 || props.clearable)
);
// "No labels match your search. Clear search": the woot's sentences have no full stop of their own.
const emptySentence = computed(() =>
  props.clearable && !/[.!?]$/.test(props.noDataMessage)
    ? `${props.noDataMessage}.`
    : props.noDataMessage
);
const isPinned = index =>
  props.pinLastColumn && index === props.headers.length - 1;
</script>

<!-- Rebuilt on the kit's table (phase 9.2, the kit's RecordsTable): registry Table parts, the first column in
     medium weight and the others muted (BaseTableRow), long text wrapping (BaseTableCell `wrap`), the row menu
     pinned to the end edge (table-pin), and "Nothing found. Clear search" when a search matches nothing.
     Same props and slots as before (`headers`, `items`, `#row`, `#header-N`). -->
<template>
  <Table class="min-w-full">
    <TableHeader v-if="showHeaders">
      <TableRow :class="pinnedRow">
        <TableHead
          v-for="(header, index) in headers"
          :key="index"
          class="text-start"
          :class="isPinned(index) ? [pinnedCell, 'w-px'] : ''"
        >
          <slot :name="`header-${index}`" :header="header">
            <span :class="{ 'sr-only': isPinned(index) }">{{ header }}</span>
          </slot>
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <template v-if="items.length">
        <slot name="row" :items="items" />
      </template>
      <TableRow v-else-if="noDataMessage && !loading">
        <TableCell
          :colspan="headers.length || 1"
          class="whitespace-normal py-8 text-center text-polaris-muted-foreground"
        >
          {{ emptySentence }}
          <Button
            v-if="clearable"
            variant="link"
            size="sm"
            class="ms-1 h-auto min-h-6 p-0"
            @click="$emit('clear')"
          >
            {{ t('SETTINGS_TABLE.CLEAR_SEARCH') }}
          </Button>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</template>
